/**
 * Security utility functions for Sprezzatura application
 */

/**
 * Rate limiting for authentication attempts
 * Tracks login attempts by IP and email to prevent brute force attacks
 */
export const authRateLimiter = (() => {
  // In-memory store for tracking attempts (in production, use Redis or similar)
  const attempts = {};
  
  // Configuration
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds
  
  /**
   * Check if an authentication attempt should be allowed
   * @param {string} identifier - Email or username attempting to authenticate
   * @param {string} ip - IP address of the request
   * @returns {Object} - { allowed: boolean, remainingAttempts: number, lockoutTime: number }
   */
  const checkAttempt = (identifier, ip) => {
    const key = `${identifier}:${ip}`;
    const now = Date.now();
    
    // Initialize or get current attempts record
    if (!attempts[key]) {
      attempts[key] = {
        count: 0,
        lastAttempt: now,
        lockedUntil: null
      };
    }
    
    const record = attempts[key];
    
    // Check if currently locked out
    if (record.lockedUntil && now < record.lockedUntil) {
      return {
        allowed: false,
        remainingAttempts: 0,
        lockoutTime: Math.ceil((record.lockedUntil - now) / 1000)
      };
    }
    
    // If was locked but lockout period expired, reset
    if (record.lockedUntil && now >= record.lockedUntil) {
      record.count = 0;
      record.lockedUntil = null;
    }
    
    // Update attempt record
    record.lastAttempt = now;
    record.count += 1;
    
    // Check if max attempts reached
    if (record.count >= MAX_ATTEMPTS) {
      record.lockedUntil = now + LOCKOUT_TIME;
      return {
        allowed: false,
        remainingAttempts: 0,
        lockoutTime: LOCKOUT_TIME / 1000
      };
    }
    
    return {
      allowed: true,
      remainingAttempts: MAX_ATTEMPTS - record.count,
      lockoutTime: 0
    };
  };
  
  /**
   * Reset attempts for an identifier after successful login
   */
  const resetAttempts = (identifier, ip) => {
    const key = `${identifier}:${ip}`;
    if (attempts[key]) {
      attempts[key].count = 0;
      attempts[key].lockedUntil = null;
    }
  };
  
  // Clean up old entries periodically (every hour)
  setInterval(() => {
    const now = Date.now();
    Object.keys(attempts).forEach(key => {
      const record = attempts[key];
      // Remove records older than 24 hours
      if (now - record.lastAttempt > 24 * 60 * 60 * 1000) {
        delete attempts[key];
      }
    });
  }, 60 * 60 * 1000);
  
  return {
    checkAttempt,
    resetAttempts
  };
})();

/**
 * Generate a CSRF token for form protection
 * @returns {string} - CSRF token
 */
export const generateCSRFToken = () => {
  // Generate a random string
  const randomBytes = new Uint8Array(32);
  window.crypto.getRandomValues(randomBytes);
  const token = Array.from(randomBytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
  
  // Store in session storage
  sessionStorage.setItem('csrfToken', token);
  
  return token;
};

/**
 * Validate a CSRF token from a form submission
 * @param {string} token - Token from the form
 * @returns {boolean} - Whether the token is valid
 */
export const validateCSRFToken = (token) => {
  const storedToken = sessionStorage.getItem('csrfToken');
  
  // Use constant-time comparison to prevent timing attacks
  if (!storedToken || !token || storedToken.length !== token.length) {
    return false;
  }
  
  let result = 0;
  for (let i = 0; i < storedToken.length; i++) {
    result |= storedToken.charCodeAt(i) ^ token.charCodeAt(i);
  }
  
  return result === 0;
};

/**
 * Generate Content Security Policy headers
 * @returns {Object} - CSP headers object
 */
export const getCSPDirectives = () => {
  // Use the correct Shopify domain
  const shopifyDomain = 'ilsprezzatura.com';
  
  return {
    'default-src': ["'self'", "https://ilsprezzatura.com"],
    'script-src': [
      "'self'", 
      "'unsafe-inline'", 
      "'unsafe-eval'", 
      "https://www.googletagmanager.com",
      "https://www.google-analytics.com",
      "https://apis.google.com",
      "https://identitytoolkit.googleapis.com",
      "https://securetoken.googleapis.com",
      "https://ilsprezzatura.com"
    ],
    'style-src': ["'self'", "'unsafe-inline'", "https://ilsprezzatura.com"],
    'img-src': ["'self'", "data:", "https://*.shopify.com", "https://www.google-analytics.com", "https://ilsprezzatura.com"],
    'font-src': ["'self'", "data:", "https://ilsprezzatura.com", "https://fonts.gstatic.com"],
    'connect-src': [
      "'self'", 
      "https://*.shopify.com",
      "https://*.myshopify.com",
      `https://${shopifyDomain}`,
      "https://firestore.googleapis.com",
      "https://www.googleapis.com",
      "https://securetoken.googleapis.com",
      "https://firebase.googleapis.com",
      "https://identitytoolkit.googleapis.com",
      "https://www.google-analytics.com",
      "https://apis.google.com",
      "https://shop.app",
      "https://shop.app/checkout/*",
      "https://shop.app/*/checkout/*",
      "https://shop.app/*/private_access_tokens*",
      "https://shop.app/checkout/*/private_access_tokens*",
      "https://ilsprezzatura.com",
      "https://ilsprezzatura.com/sf_private_access_tokens",
      "https://*.shopifycs.com"
    ],
    'frame-src': [
      "'self'", 
      "https://checkout.shopify.com",
      "https://apis.google.com",
      "https://sprezzaturawebsite.firebaseapp.com",
      "https://identitytoolkit.googleapis.com",
      "https://securetoken.googleapis.com",
      `https://${shopifyDomain}`,
      "https://shop.app",
      "https://shop.app/checkout/*",
      "https://shop.app/*/private_access_tokens*",
      "https://ilsprezzatura.com",
      "https://ilsprezzatura.com/sf_private_access_tokens"
    ],
    'object-src': ["'none'"],
    'form-action': [
      "'self'",
      "https://*.shopify.com",
      "https://*.myshopify.com",
      `https://${shopifyDomain}`,
      "https://checkout.shopify.com",
      "https://shop.app",
      "https://shop.app/checkout/*",
      "https://ilsprezzatura.com",
      "https://ilsprezzatura.com/sf_private_access_tokens",
      "https://identitytoolkit.googleapis.com",
      "https://securetoken.googleapis.com"
    ]
  };
};

/**
 * Apply CSP meta tag to document
 */
export const applyCSP = () => {
  const directives = getCSPDirectives();
  let cspString = '';
  
  // Convert directives object to CSP string
  Object.entries(directives).forEach(([directive, sources]) => {
    cspString += `${directive} ${sources.join(' ')}; `;
  });
  
  // Create or update CSP meta tag
  let cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (!cspMeta) {
    cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    document.head.appendChild(cspMeta);
  }
  cspMeta.content = cspString.trim();
};
